import { formatCents, formatPercent, bpsToPercent } from '@affily/shared/money';
import { useFetch } from '../../hooks/useFetch.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { Badge, Spinner, EmptyState, Button } from '../../components/ui.jsx';

const statusTone = {
  pending: 'neutral',
  approved: 'accent',
  settled: 'caution',
  paid: 'positive',
  reversed: 'negative',
};

/**
 * The commission ledger.
 *
 * Every line shows gross, fee and net. A creator should never have to work out
 * where the difference between "earned" and "received" went — including the
 * reversed lines, which stay visible with their reason rather than quietly
 * vanishing.
 */
export default function CreatorEarnings() {
  const { data, loading } = useFetch('/dashboard/creator/commissions');
  const commissions = data?.commissions ?? [];

  // Gifts are goods, not money: counted on their own, never added to anything above.
  const gifts = useFetch('/gift-requests?side=creator');
  const year = new Date().getFullYear();
  const received = (gifts.data?.giftRequests ?? []).filter(
    (r) => ['sent', 'redeemed'].includes(r.status) && r.sentAt && new Date(r.sentAt).getFullYear() === year,
  );
  const statedCents = received.reduce((sum, r) => sum + (r.gift.retailValueCents ?? 0), 0);
  const unvalued = received.filter((r) => r.gift.retailValueCents == null).length;

  return (
    <DashboardLayout
      side="creator"
      title="Earnings"
      description="Every commission, with the fee shown on its own line."
    >
      {loading ? (
        <Spinner />
      ) : commissions.length === 0 ? (
        <EmptyState title="No commissions yet" action={<Button to="/programs">Find a program</Button>}>
          Commissions appear here as soon as a tracked conversion is attributed to one of your
          links.
        </EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Program</th>
                <th>Rate applied</th>
                <th className="num">Order</th>
                <th className="num">Gross</th>
                <th className="num">Fee</th>
                <th className="num">Net</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {commissions.map((c) => (
                <tr key={c.id} className={c.status === 'reversed' ? 'opacity-60' : ''}>
                  <td className="whitespace-nowrap text-ink-muted">
                    {new Date(c.occurredAt).toLocaleDateString()}
                  </td>
                  <td>
                    <span className="font-medium">{c.program?.title ?? '—'}</span>
                    {c.brand && (
                      <span className="block text-xs text-ink-faint">{c.brand.companyName}</span>
                    )}
                    {c.prize && <span className="block text-xs text-accent">Prize: {c.prize}</span>}
                    {c.post && (
                      <a href={c.post} target="_blank" rel="noopener noreferrer nofollow" className="block max-w-56 truncate text-xs text-accent hover:underline">
                        Post approved: {c.post}
                      </a>
                    )}
                  </td>
                  <td className="tabular-nums">
                    {/* The rate that was actually applied, from the frozen
                        snapshot — not whatever the program says today. */}
                    {c.appliedRateBps != null
                      ? formatPercent(bpsToPercent(c.appliedRateBps), { digits: 2 })
                      : formatCents(c.appliedFlatCents, { currency: c.currency })}
                  </td>
                  <td className="num text-ink-muted">
                    {c.orderAmountCents ? formatCents(c.orderAmountCents, { currency: c.currency }) : '—'}
                  </td>
                  <td className="num">{formatCents(c.grossAmountCents, { currency: c.currency })}</td>
                  <td className="num text-ink-muted">−{formatCents(c.platformFeeCents, { currency: c.currency })}</td>
                  <td className="num font-medium">{formatCents(c.netAmountCents, { currency: c.currency })}</td>
                  <td>
                    <Badge tone={statusTone[c.status]}>{c.status}</Badge>
                    {c.reversalReason && (
                      <span className="block text-xs text-negative">{c.reversalReason}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {received.length > 0 && (
        <section
          aria-labelledby="gifts-received"
          className="mt-8 rounded-lg border border-dashed border-rule-strong bg-paper-sunken p-4"
        >
          <h2 id="gifts-received" className="text-sm font-semibold">
            Gifts received in {year}
          </h2>
          <p className="mt-1 text-sm text-ink">
            <strong className="tabular-nums">{received.length}</strong> gift
            {received.length === 1 ? '' : 's'} sent to you
            {statedCents > 0 && (
              <>
                , worth <strong className="tabular-nums">{formatCents(statedCents)}</strong> by the
                brands’ own figures
              </>
            )}
            {unvalued > 0 && ` (${unvalued} with no stated value)`}.
          </p>
          <p className="mt-2 text-xs text-ink-muted">
            This is not money and is not part of your earnings, payouts or the totals above. It is
            the brand-stated retail value, not an assessment of anything you owe. In many countries
            gifts received for promotion count as taxable income, and you should disclose them as
            gifted when you post.
          </p>
        </section>
      )}
    </DashboardLayout>
  );
}
